import { useState, useEffect, useRef } from 'react';
import { calculateStickyIndex } from '../PhotoDamageAnalyzer/utils';

export interface MessagePair {
  user: any;
  assistant: any | null;
  userIndex: number;
  assistantIndex: number | null;
}

interface UseStickyHeaderParams {
  scrollRootRef: React.RefObject<HTMLDivElement | null>;
  messageRefs: React.RefObject<Record<number, HTMLDivElement | null>>;
  pairs: MessagePair[];
  messages: any[];
}

/**
 * Custom hook to manage sticky header visibility
 * Uses IntersectionObserver to track which assistant messages are visible
 */
export const useStickyHeader = ({
  scrollRootRef,
  messageRefs,
  pairs,
  messages,
}: UseStickyHeaderParams) => {
  const [stickyIndex, setStickyIndex] = useState<number | null>(null);
  const visibleAssistantsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    // Clear the previous set of visible assistants
    visibleAssistantsRef.current.clear();

    // Create intersection observer to detect visible messages
    const intersectionObserver = new IntersectionObserver(
      (intersectionEntries) => {
        intersectionEntries.forEach((entry) => {
          const currentAssistantIndex = Number(
            entry.target.getAttribute('data-index'),
          );
          if (Number.isFinite(currentAssistantIndex)) {
            if (entry.isIntersecting) {
              visibleAssistantsRef.current.add(currentAssistantIndex);
            } else {
              visibleAssistantsRef.current.delete(currentAssistantIndex);
            }
          }
        });

        // Calculate the sticky index based on the visible assistants
        const stickyIndex = calculateStickyIndex(
          visibleAssistantsRef.current,
          pairs,
        );
        setStickyIndex(stickyIndex);
      },
      {
        root: scrollRootRef.current || null,
        threshold: 0.1,
      },
    );

    // Observe each assistant message to detect visibility
    pairs.forEach((messagePair) => {
      if (messagePair.assistantIndex !== null) {
        const assistantMessageElement =
          messageRefs.current?.[messagePair.assistantIndex];
        if (assistantMessageElement) {
          assistantMessageElement.setAttribute(
            'data-index',
            messagePair.assistantIndex.toString(),
          );
          intersectionObserver.observe(assistantMessageElement);
        }
      }
    });

    // Clean up the observer when unmounting
    return () => intersectionObserver.disconnect();
  }, [messages, pairs]);

  return stickyIndex;
};
